import * as React from 'react'
import { handleImageUpload } from '@/tiptap/lib/tiptap-utils'
import { SettingsField } from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import { Editor } from '@tiptap/react'
import { Upload, Link, Image as ImageIcon, Sun, Moon } from 'lucide-react'
import { toast } from 'sonner'

interface InlineImageDialogProps {
  editor: Editor
  isOpen: boolean
  onClose: () => void
  onUpload?: (file: File) => Promise<string>
  slashCommandRange?: { from: number; to: number } | null
  replaceMode?: boolean
  existingImage?: {
    src: string
    alt?: string
    title?: string
    darkSrc?: string
    display?: 'inline' | 'original' | 'block'
    nodePos?: number
  }
  isDarkModeReplace?: boolean
}

export function InlineImageDialog({
  editor,
  isOpen,
  onClose,
  onUpload,
  slashCommandRange,
  replaceMode = false,
  existingImage,
  isDarkModeReplace = false,
}: InlineImageDialogProps) {
  const [imageUrl, setImageUrl] = React.useState('')
  const [darkImageUrl, setDarkImageUrl] = React.useState('')
  const [altText, setAltText] = React.useState('')
  const [isUploading, setIsUploading] = React.useState(false)
  const [uploadedUrl, setUploadedUrl] = React.useState('')
  const [uploadedDarkUrl, setUploadedDarkUrl] = React.useState('')
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const darkFileInputRef = React.useRef<HTMLInputElement>(null)
  const [tab, setTab] = React.useState<'url' | 'upload'>('url')

  // Initialize with existing image data when dialog opens in replace mode
  React.useEffect(() => {
    if (isOpen && replaceMode && existingImage) {
      setImageUrl(isDarkModeReplace ? '' : existingImage.src || '')
      setDarkImageUrl(existingImage.darkSrc || '')
      setAltText(existingImage.alt || '')
    }
  }, [isOpen, replaceMode, existingImage, isDarkModeReplace])

  const handleFileUpload = async (file: File, isDark = false) => {
    // Use custom upload handler if provided, otherwise use default
    const uploadHandler = onUpload || handleImageUpload

    setIsUploading(true)
    try {
      const url = await uploadHandler(file)
      if (isDark) {
        setUploadedDarkUrl(url)
        setDarkImageUrl(url)
      } else {
        setUploadedUrl(url)
        setImageUrl(url)
      }
      toast.success(
        isDark ? 'Dark mode image uploaded' : 'Image uploaded successfully'
      )
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isDark = false
  ) => {
    const file = e.target.files?.[0]
    if (file && /image/i.test(file.type)) {
      handleFileUpload(file, isDark)
    }
  }

  const handleInsert = () => {
    const src = imageUrl || uploadedUrl
    const darkSrc = darkImageUrl || uploadedDarkUrl

    // In replace mode, we need either a new src or a new darkSrc
    if (replaceMode) {
      if (isDarkModeReplace && !darkSrc) {
        toast.error('Please provide a dark mode image URL or upload an image')
        return
      } else if (!isDarkModeReplace && !src) {
        toast.error('Please provide an image URL or upload an image')
        return
      }
    } else if (!src) {
      toast.error('Please provide an image URL or upload an image')
      return
    }

    // Handle replace mode
    if (replaceMode && existingImage && existingImage.nodePos !== undefined) {
      const updateAttrs: any = {
        alt: altText || existingImage.alt || 'image',
      }

      if (isDarkModeReplace) {
        updateAttrs.darkSrc = darkSrc
      } else {
        updateAttrs.src = src
        if (darkSrc) updateAttrs.darkSrc = darkSrc
      }

      editor
        .chain()
        .focus()
        .setNodeSelection(existingImage.nodePos)
        .updateAttributes('inlineImage', updateAttrs)
        .run()
    } else if (slashCommandRange) {
      // If this is from a slash command, delete the slash command text first
      editor
        .chain()
        .focus()
        .deleteRange(slashCommandRange)
        .insertInlineImage({
          src,
          alt: altText || 'image',
          darkSrc: darkSrc || undefined,
          display: 'inline',
        })
        .run()
    } else {
      // Normal insertion without slash command
      editor
        .chain()
        .focus()
        .insertInlineImage({
          src,
          alt: altText || 'image',
          darkSrc: darkSrc || undefined,
          display: 'inline',
        })
        .run()
    }

    handleClose()
  }

  const handleClose = () => {
    setImageUrl('')
    setDarkImageUrl('')
    setAltText('')
    setUploadedUrl('')
    setUploadedDarkUrl('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='gap-6 sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>
            {replaceMode
              ? isDarkModeReplace
                ? 'Replace Dark Mode Image'
                : 'Replace Image'
              : 'Insert Inline Image'}
          </DialogTitle>
          <DialogDescription>
            Paste an image URL or choose a file to upload
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 px-6'>
          <SettingsField label='Alt Text' htmlFor='alt-text'>
            <Input
              id='alt-text'
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder='Describe the image...'
            />
          </SettingsField>

          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as 'url' | 'upload')}
            className='w-full gap-6'
          >
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger
                value='url'
                className='text-sm leading-5 font-medium'
              >
                <Link className='mr-0.5 h-4 w-4' />
                URL
              </TabsTrigger>
              <TabsTrigger
                value='upload'
                className='text-sm leading-5 font-medium'
              >
                <Upload className='mr-0.5 h-4 w-4' />
                Upload
              </TabsTrigger>
            </TabsList>

            <TabsContent value='url' className='min-h-36 space-y-6'>
              <SettingsField label='Image URL' htmlFor='image-url'>
                <Input
                  id='image-url'
                  type='url'
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder='https://example.com/image.png'
                />
              </SettingsField>

              <SettingsField
                label='Dark Mode Image URL (Optional)'
                htmlFor='dark-image-url'
              >
                <Input
                  id='dark-image-url'
                  type='url'
                  value={darkImageUrl}
                  onChange={(e) => setDarkImageUrl(e.target.value)}
                  placeholder='https://example.com/image-dark.png'
                />
              </SettingsField>
            </TabsContent>

            <TabsContent value='upload' className='min-h-36'>
              <SettingsField label='Upload image'>
                <div className='flex w-full items-center gap-2'>
                  <div>
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, false)}
                      className='hidden'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      size='md'
                      className='text-sm leading-5 font-medium'
                    >
                      <Upload className='h-4 w-4' />
                      Choose Image
                    </Button>
                  </div>
                  {/* 
                  {uploadedUrl && (
                    <span className='text-muted-foreground text-sm'>
                      Image uploaded
                    </span>
                  )} */}
                  <div>
                    <input
                      ref={darkFileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, true)}
                      className='hidden'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='md'
                      onClick={() => darkFileInputRef.current?.click()}
                      disabled={isUploading}
                      className='text-sm leading-5 font-medium'
                    >
                      <Upload className='h-4 w-4' />
                      Choose Dark Image
                    </Button>
                    {/* {uploadedDarkUrl && (
                        <span className='text-muted-foreground text-sm'>
                          Dark image uploaded
                        </span>
                      )} */}
                  </div>
                </div>
                <span className='text-muted-foreground text-sm leading-5'>
                  A default image will be used in dark mode if a dark version is
                  not provided.
                </span>
              </SettingsField>
            </TabsContent>
          </Tabs>

          {/* {(imageUrl || uploadedUrl) && (
            <div className='bg-muted/50 rounded-md border p-4'>
              <Label className='text-sm'>Preview</Label>
              <div className='mt-2 flex items-center gap-4'>
                <img
                  src={imageUrl || uploadedUrl}
                  alt={altText || 'Preview'}
                  className='inline max-h-8 w-auto rounded-sm'
                />
                {(darkImageUrl || uploadedDarkUrl) && (
                  <>
                    <span className='text-muted-foreground text-sm'>→</span>
                    <div className='dark'>
                      <img
                        src={darkImageUrl || uploadedDarkUrl}
                        alt={altText || 'Dark mode preview'}
                        className='inline max-h-8 w-auto rounded-sm'
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )} */}

          {/* Preview */}
          {(imageUrl || uploadedUrl) && (
            <SettingsField label='Preview'>
              <div className='bg-muted flex items-center justify-between gap-6 rounded-md border p-4'>
                <div className='flex-1 space-y-2'>
                  <div className='flex w-full items-center gap-2'>
                    <Sun className='text-muted-foreground h-4 w-4' />
                    <span className='text-muted-foreground text-xs leading-4'>
                      Light Mode
                    </span>
                  </div>
                  <img
                    src={imageUrl || uploadedUrl}
                    alt={altText || 'Preview'}
                    className='inline max-h-20 w-auto rounded-md'
                  />
                </div>
                {(darkImageUrl || uploadedDarkUrl) && (
                  <div className='flex-1 space-y-2'>
                    <div className='flex w-full items-center gap-2'>
                      <Moon className='text-muted-foreground h-4 w-4' />
                      <span className='text-muted-foreground text-xs leading-4'>
                        Dark Mode
                      </span>
                    </div>
                    <img
                      src={darkImageUrl || uploadedDarkUrl}
                      alt={altText || 'Dark mode preview'}
                      className='inline max-h-20 w-auto rounded-md'
                    />
                  </div>
                )}
              </div>
            </SettingsField>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleInsert}
            disabled={replaceMode ? false : !imageUrl && !uploadedUrl}
          >
            {replaceMode ? 'Replace Image' : 'Insert Image'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
